import type {
    AddItemsRequest,
    CreateDatasetRequest,
    Dataset,
    DatasetDocumentsResponse,
    DatasetImagesResponse,
    DatasetListResponse,
    DatasetPreviewRequest,
    DatasetPreviewResponse,
    QueryDocumentsRequest,
    QueryDocumentsResponse,
    RemoveItemsRequest,
    UpdateDatasetRequest,
} from '@packages/types';
import { api } from '@ui/app/api';

export interface GetDatasetItemsParams {
    id: string;
    page?: number;
    pageSize?: number;
}

export interface AddItemsParams {
    id: string;
    data: AddItemsRequest;
}

export interface RemoveItemsParams {
    id: string;
    data: RemoveItemsRequest;
}

export interface DocumentFilterValuesParams {
    search?: string;
    limit?: number;
}

export interface DocumentFilterValue {
    value: string;
    label: string;
    concept_id: number;
    icd_code?: string;
    count: number;
}

export interface DocumentFilterValuesResponse {
    field: string;
    values: DocumentFilterValue[];
    total: number;
}

export interface UpdateDatasetParams {
    id: string;
    data: UpdateDatasetRequest;
}

// Raw extractions types
export interface RawDocument {
    document_id: string;
    file_name: string;
    file_path: string;
    file_type: string;
    file_metadata?: Record<string, unknown>;
    timestamp?: string;
    content?: unknown;
}

export interface PatientRecord {
    id: string;
    name: string;
    type: 'note';
    date: string;
    patientId: string;
    dataSource: string;
    documentType: string;
    documentId: string;
    isRandom?: boolean;
}

export interface Patient {
    id: string;
    name: string;
    age: number;
    gender: string;
    records: PatientRecord[];
    documentCount?: number;
}

interface PaginationInfo {
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
}

export interface RawExtractionsDocumentResponse extends PaginationInfo {
    documents: RawDocument[];
}

export interface RawExtractionsPatientResponse extends PaginationInfo {
    patients: Patient[];
}

export type RawExtractionsResponse = RawExtractionsDocumentResponse | RawExtractionsPatientResponse;

export interface GetRawExtractionsParams {
    limit?: number;
    offset?: number;
    search?: string;
    patientIds?: number[];
    noteTypes?: string[];
    jobIds?: string[];
    noteDateStart?: string;
    noteDateEnd?: string;
    groupByPatient?: boolean;
    excludedIds?: number[];
    // New NER/annotation filters
    entities?: string[];
    entityTexts?: string[];
    entityTextMode?: 'exact' | 'contains'; // NEW: Entity text search mode
    entityTextSection?: string;
    entityTextSentenceContains?: string;
    assertions?: string[];
    omopConcepts?: string[];
    omopConceptClasses?: string[];
    omopMinConfidence?: number;
    omopStandardStatus?: string;
    // Document count filtering
    minDocumentCount?: number;
    maxDocumentCount?: number;
    // Confidence filtering
    nerConfidenceEntities?: string[];
    nerConfidenceMin?: number;
    nerConfidenceMax?: number;
    assertionConfidenceEntities?: string[];
    assertionConfidenceMin?: number;
    assertionConfidenceMax?: number;
    datasetId?: string; // If provided, filter by documents in this dataset (from dataset_documents table)
}

export const datasetsApi = api.injectEndpoints({
    endpoints: builder => ({
        // Get raw document extractions with optional patient grouping
        getRawExtractions: builder.query<RawExtractionsResponse, GetRawExtractionsParams>({
            query: params => {
                const searchParams = new URLSearchParams();

                if (params.limit) searchParams.set('limit', params.limit.toString());
                if (params.offset) searchParams.set('offset', params.offset.toString());
                if (params.search) searchParams.set('search', params.search);
                if (params.noteDateStart) searchParams.set('noteDateStart', params.noteDateStart);
                if (params.noteDateEnd) searchParams.set('noteDateEnd', params.noteDateEnd);
                if (params.groupByPatient) searchParams.set('groupByPatient', 'true');

                params.patientIds?.forEach(id => searchParams.append('patientIds', id.toString()));
                params.noteTypes?.forEach(type => searchParams.append('noteTypes', type));
                params.jobIds?.forEach(id => searchParams.append('jobIds', id));
                params.excludedIds?.forEach(id => searchParams.append('excludedIds', id.toString()));

                // New NER/annotation filter parameters
                params.entities?.forEach(entity => searchParams.append('entities', entity));
                params.entityTexts?.forEach(text => searchParams.append('entityTexts', text));
                if (params.entityTextMode) searchParams.set('entityTextMode', params.entityTextMode);
                if (params.entityTextSection) searchParams.set('entityTextSection', params.entityTextSection);
                if (params.entityTextSentenceContains)
                    searchParams.set('entityTextSentenceContains', params.entityTextSentenceContains);
                params.assertions?.forEach(assertion => searchParams.append('assertions', assertion));
                params.omopConcepts?.forEach(concept => searchParams.append('omopConcepts', concept));
                params.omopConceptClasses?.forEach(c => searchParams.append('omopConceptClasses', c));
                if (params.omopMinConfidence !== undefined)
                    searchParams.set('omopMinConfidence', params.omopMinConfidence.toString());
                if (params.omopStandardStatus) searchParams.set('omopStandardStatus', params.omopStandardStatus);

                // Document count filtering parameters
                if (params.minDocumentCount) searchParams.set('minDocumentCount', params.minDocumentCount.toString());
                if (params.maxDocumentCount) searchParams.set('maxDocumentCount', params.maxDocumentCount.toString());

                // Confidence filtering parameters
                params.nerConfidenceEntities?.forEach(entity => searchParams.append('nerConfidenceEntities', entity));
                if (params.nerConfidenceMin !== undefined)
                    searchParams.set('nerConfidenceMin', params.nerConfidenceMin.toString());
                if (params.nerConfidenceMax !== undefined)
                    searchParams.set('nerConfidenceMax', params.nerConfidenceMax.toString());
                params.assertionConfidenceEntities?.forEach(entity =>
                    searchParams.append('assertionConfidenceEntities', entity)
                );
                if (params.assertionConfidenceMin !== undefined)
                    searchParams.set('assertionConfidenceMin', params.assertionConfidenceMin.toString());
                if (params.assertionConfidenceMax !== undefined)
                    searchParams.set('assertionConfidenceMax', params.assertionConfidenceMax.toString());

                const queryString = searchParams.toString();
                return `/documents/raw-extractions${queryString ? `?${queryString}` : ''}`;
            },
            providesTags: ['Documents'],
        }),

        // Get all datasets for the current user with pagination and filters
        getDatasets: builder.query<
            DatasetListResponse,
            {
                page?: number;
                pageSize?: number;
                name?: string;
                description?: string;
                status?: string[];
                cohortIds?: string[];
                orderBy?: string;
                orderDirection?: 'asc' | 'desc';
                sendMockData?: boolean;
            }
        >({
            query: params => {
                const queryParams = new URLSearchParams();
                queryParams.append('page', String(params?.page ?? 1));
                queryParams.append('pageSize', String(params?.pageSize ?? 10));
                if (params?.name) queryParams.append('name', params.name);
                if (params?.description) queryParams.append('description', params.description);
                if (params?.status && params.status.length > 0) {
                    params.status.forEach(s => queryParams.append('status', s));
                }
                if (params?.cohortIds && params.cohortIds.length > 0) {
                    params.cohortIds.forEach(id => queryParams.append('cohortIds', id));
                }
                if (params?.orderBy) queryParams.append('orderBy', params.orderBy);
                if (params?.orderDirection) queryParams.append('orderDirection', params.orderDirection);
                if (params?.sendMockData === true) {
                    queryParams.append('sendMockData', 'true');
                }
                const queryString = queryParams.toString();
                return `/datasets${queryString ? `?${queryString}` : ''}`;
            },
            providesTags: ['Datasets'],
        }),

        // Get aggregate stats for datasets (total datasets, documents, images, patients)
        getDatasetStats: builder.query<
            { totalDatasets: number; totalDocuments: number; totalImages: number; totalPatients: number },
            void
        >({
            query: () => '/datasets/stats',
            providesTags: ['Datasets'],
        }),

        // Get a single dataset by ID
        getDataset: builder.query<Dataset, string>({
            query: id => `/datasets/${id}`,
            providesTags: (_result, _error, id) => [{ type: 'Datasets', id }],
        }),

        // Create a new dataset
        createDataset: builder.mutation<Dataset, CreateDatasetRequest>({
            query: body => ({
                url: '/datasets',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Datasets'],
        }),

        // Update an existing dataset
        updateDataset: builder.mutation<Dataset, UpdateDatasetParams>({
            query: ({ id, data }) => ({
                url: `/datasets/${id}`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: (_result, _error, { id }) => ['Datasets', { type: 'Datasets', id }],
        }),

        // Delete a dataset
        deleteDataset: builder.mutation<{ message: string }, string>({
            query: id => ({
                url: `/datasets/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Datasets'],
        }),

        // Get dataset dependencies (curation jobs that use this dataset)
        getDatasetDependencies: builder.query<{ curationJobs: { id: string; name: string; status: string }[] }, string>(
            {
                query: id => `/datasets/${id}/dependencies`,
            }
        ),

        // Get dataset items (documents and images)
        getDatasetImages: builder.query<DatasetImagesResponse, GetDatasetItemsParams>({
            query: ({ id, page = 1, pageSize = 50 }) => {
                const queryParams = new URLSearchParams({
                    page: page.toString(),
                    pageSize: pageSize.toString(),
                });
                return `/datasets/${id}/images?${queryParams.toString()}`;
            },
            providesTags: (_result, _error, { id }) => [{ type: 'DatasetItems', id }],
        }),

        getDatasetDocuments: builder.query<DatasetDocumentsResponse, GetDatasetItemsParams>({
            query: ({ id, page = 1, pageSize = 50 }) => {
                const queryParams = new URLSearchParams({
                    page: page.toString(),
                    pageSize: pageSize.toString(),
                });
                return `/datasets/${id}/documents?${queryParams.toString()}`;
            },
            providesTags: (_result, _error, { id }) => [{ type: 'DatasetItems', id }],
        }),

        // Add items to a dataset
        addDatasetItems: builder.mutation<Dataset, AddItemsParams>({
            query: ({ id, data }) => ({
                url: `/datasets/${id}/items`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (_result, _error, { id }) => [
                { type: 'Datasets', id },
                { type: 'DatasetItems', id },
            ],
        }),

        // Remove items from a dataset
        removeDatasetItems: builder.mutation<Dataset, RemoveItemsParams>({
            query: ({ id, data }) => ({
                url: `/datasets/${id}/items`,
                method: 'DELETE',
                body: data,
            }),
            invalidatesTags: (_result, _error, { id }) => [
                'Datasets', // Invalidate dataset list to update counts
                { type: 'Datasets', id },
                { type: 'DatasetItems', id },
            ],
        }),

        // Preview dataset (get counts before creating)
        previewDataset: builder.mutation<DatasetPreviewResponse, DatasetPreviewRequest>({
            query: body => ({
                url: '/datasets/preview',
                method: 'POST',
                body,
            }),
        }),

        // Preview patients for an ingestion filter (used by dataset creation with raw data)
        getIngestionPreview: builder.query<
            {
                patients: Array<{
                    id: string;
                    name: string;
                    age: number;
                    gender: string;
                    documentCount?: number;
                    records?: Array<{
                        id: string;
                        name: string;
                        type: 'note';
                        date: string;
                        patientId: string;
                        dataSource: string;
                        documentType: string;
                        noteId: number;
                        matched?: boolean;
                        matchedConditions?: string[];
                        isRandom?: boolean;
                    }>;
                }>;
                totalPatients: number;
                documentCount: number;
            },
            {
                ingestion_filter: any;
                limit?: number;
                offset?: number;
                excludedPatientIds?: string[];
                onlyMatchedDocuments?: boolean;
                datasetId?: string;
            }
        >({
            query: body => ({
                url: 'datasets/ingestion-preview',
                method: 'POST',
                body,
            }),
            keepUnusedDataFor: 5 * 60,
            serializeQueryArgs: ({ endpointName, queryArgs }) => {
                const sortedArgs = {
                    ...queryArgs,
                    excludedPatientIds: queryArgs.excludedPatientIds
                        ? [...queryArgs.excludedPatientIds].sort()
                        : undefined,
                };
                return `${endpointName}(${JSON.stringify(sortedArgs)})`;
            },
        }),

        // Export dataset
        exportDataset: builder.mutation<
            Blob,
            { id: string; format: 'json' | 'csv' | 'manifest'; includeDocuments?: boolean; includeImages?: boolean }
        >({
            query: ({ id, format, includeDocuments = true, includeImages = true }) => ({
                url: `/datasets/${id}/export?format=${format}&includeDocuments=${includeDocuments}&includeImages=${includeImages}`,
                method: 'GET',
                responseHandler: async response => {
                    return response.blob();
                },
            }),
        }),

        // Query documents based on filter (for real-time preview during dataset creation)
        queryDocuments: builder.query<QueryDocumentsResponse, QueryDocumentsRequest>({
            query: params => ({
                url: '/datasets/documents/query',
                method: 'POST',
                body: params,
            }),
            // Don't cache this query as it's for dynamic filtering
            keepUnusedDataFor: 0,
        }),

        // Get diagnosis filter values with document counts
        getDocumentDiagnosisValues: builder.query<DocumentFilterValuesResponse, DocumentFilterValuesParams>({
            query: params => {
                const queryParams = new URLSearchParams();
                if (params.search) queryParams.set('search', params.search);
                if (params.limit) queryParams.set('limit', params.limit.toString());
                const queryString = queryParams.toString();
                return `/datasets/filters/diagnosis/values${queryString ? `?${queryString}` : ''}`;
            },
        }),

        // Get procedure filter values with document counts
        getDocumentProcedureValues: builder.query<DocumentFilterValuesResponse, DocumentFilterValuesParams>({
            query: params => {
                const queryParams = new URLSearchParams();
                if (params.search) queryParams.set('search', params.search);
                if (params.limit) queryParams.set('limit', params.limit.toString());
                const queryString = queryParams.toString();
                return `/datasets/filters/procedure/values${queryString ? `?${queryString}` : ''}`;
            },
        }),

        // Get note type filter values with document counts
        getDocumentNoteTypeValues: builder.query<DocumentFilterValuesResponse, DocumentFilterValuesParams>({
            query: params => {
                const queryParams = new URLSearchParams();
                if (params.search) queryParams.set('search', params.search);
                if (params.limit) queryParams.set('limit', params.limit.toString());
                const queryString = queryParams.toString();
                return `/datasets/filters/note-type/values${queryString ? `?${queryString}` : ''}`;
            },
        }),
    }),
});

export const {
    useGetRawExtractionsQuery,
    useGetDatasetsQuery,
    useGetDatasetStatsQuery,
    useGetDatasetQuery,
    useCreateDatasetMutation,
    useUpdateDatasetMutation,
    useDeleteDatasetMutation,
    useGetDatasetDependenciesQuery,
    useLazyGetDatasetDependenciesQuery,
    useGetDatasetImagesQuery,
    useGetDatasetDocumentsQuery,
    useAddDatasetItemsMutation,
    useRemoveDatasetItemsMutation,
    usePreviewDatasetMutation,
    useExportDatasetMutation,
    useQueryDocumentsQuery,
    useGetDocumentDiagnosisValuesQuery,
    useGetDocumentProcedureValuesQuery,
    useGetDocumentNoteTypeValuesQuery,
    useLazyGetIngestionPreviewQuery,
} = datasetsApi;
